<template>
  <div class="container mt-4">
    <!--
    <div class="text-muted mb-3 d-flex justify-content-between align-items-center">
      <span>Thread: {{ threadId || 'Not created yet' }}</span>
      <a v-if="threadId" href="#" @click.prevent="clearThread" class="text-danger small">
        <i class="bi bi-trash me-1"></i>Clear
      </a>
    </div>
    -->
    <!--
    <div class="alert alert-info mb-4">
      <strong>Note:</strong> This assistant is in beta. It may not always provide accurate or complete answers.
    </div>
    -->
    <div v-if="threadId" class="card mb-3">
      <div 
        ref="messageContainer"
        class="card-body" 
        style="max-height: 60vh; overflow-y: auto;"
      >
        <div v-for="(msg, index) in messages" :key="index" class="mb-4">
          <div class="fw-bold text-primary">{{ msg.role === 'user' ? 'You' : 'Assistant' }}:</div>
          
          <div v-if="msg.role === 'assistant' && msg.content">
            <div v-html="formatMarkdown(msg.content.main_answer, msg.content.sources)" class="formatted-content"></div>
            
            <!-- Display sources if available -->
            <div v-if="msg.content.sources && msg.content.sources.length > 0" class="mt-3">
              <p class="fw-semibold mb-1">Sources:</p>
              <ol class="ps-3">
                <li v-for="(source, idx) in msg.content.sources" :key="idx" class="mb-1">
                  <a :href="source.url" target="_blank" class="text-decoration-none">
                    {{ source.title || source.filename }}
                  </a>
                </li>
              </ol>
            </div>
          </div>

          <div v-else>
            <p>{{ msg.content }}</p>
          </div>
        </div>
        
        <!-- Thinking indicator -->
        <div v-if="isThinking" class="mb-4">
          <div class="fw-bold text-primary">Assistant:</div>
          <div class="text-muted">
            <i class="spinner-border spinner-border-sm me-2"></i>
            <span>Thinking...</span>
          </div>
        </div>
      </div>
    </div>

    <form @submit.prevent="sendMessage">
      <div class="input-group">
        <input
          type="text"
          v-model="userInput"
          class="form-control"
          placeholder="Ask the assistant..."
          :disabled="isThinking"
          required
        />
        <button class="btn btn-primary" type="submit" :disabled="isThinking">
          <span v-if="!isThinking">Send</span>
          <span v-else>
            <span class="spinner-border spinner-border-sm me-1"></span>
            Processing...
          </span>
        </button>
        <button class="btn btn-primary" type="submit" :disabled="isThinking || !threadId" @click.prevent="clearThread">
          <span>Clear</span>
        </button>
      </div>
    </form>
    
    <!-- Support ticket follow-up link -->
    <div v-if="hasAssistantReply" class="mt-3 text-center">
      <a href="#" @click.prevent="createSupportTicket" class="text-decoration-none">
        <i class="bi bi-ticket-perforated me-1"></i>
        <span v-if="!isCreatingTicket">Create support follow up</span>
        <span v-else>
          <span class="spinner-border spinner-border-sm me-1"></span>
          Creating ticket...
        </span>
      </a>
    </div>
    
    <!-- Debug Section -->
    <div v-if="showDebug" class="mt-4">
      <div class="card">
        <div class="card-header d-flex justify-content-between align-items-center">
          <span>Debug: Raw Assistant Message</span>
          <button class="btn btn-sm btn-primary" @click="testFormatting">Test Formatting</button>
        </div>
        <div class="card-body">
          <textarea 
            v-model="debugRawContent" 
            class="form-control font-monospace small"
            rows="10"
            placeholder="Paste raw assistant message here to test formatting..."
          ></textarea>
          
          <div v-if="debugFormattedContent" class="mt-3">
            <h6>Formatted Output:</h6>
            <div class="border rounded p-3 bg-light">
              <div v-html="debugFormattedContent" class="formatted-content"></div>
              
              <div v-if="debugSources && debugSources.length > 0" class="mt-3">
                <p class="fw-semibold mb-1">Sources:</p>
                <ol class="ps-3">
                  <li v-for="(source, idx) in debugSources" :key="idx" class="mb-1">
                    <a :href="source.url" target="_blank" class="text-decoration-none">
                      {{ source.title || source.filename }}
                    </a>
                  </li>
                </ol>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Toggle Debug Button -->
     <!--
    <div class="text-center mt-3">
      <button class="btn btn-sm btn-secondary" @click="showDebug = !showDebug">
        <i :class="showDebug ? 'bi bi-eye-slash' : 'bi bi-eye'"></i>
        {{ showDebug ? 'Hide' : 'Show' }} Debug Panel
      </button>
    </div>
    -->
  </div>
</template>

<script>
import websiteFiles from '@/utils/website_files_dictionary.js';
import "/src/visualcrossing-core/vccommunicator_openai";
export default {
  name: 'AiAssistant',
  data() {
    return {
      userInput: '',
      messages: [],
      threadId: null,
      isThinking: false,
      showDebug: false,
      debugRawContent: '',
      debugFormattedContent: '',
      debugSources: [],
      isCreatingTicket: false
    };
  },
  computed: {
    vcCommunicator() {
      return window.vcCommunicator || null;
    },
    hasAssistantReply() {
      return this.messages.some(msg => msg.role === 'assistant');
    }
  },
  mounted() {
    // Check if vcCommunicator is available
    if (!this.vcCommunicator) {
      console.error('VcCommunicator not available');
    }
  },
  methods: {
    async createSupportTicket() {
      if (this.isCreatingTicket) return;
      
      if (!this.vcCommunicator) {
        console.error('VcCommunicator not available');
        return;
      }

      this.isCreatingTicket = true;

      try {
        // Create the summarization request with JSON format requirement
        const summaryPrompt = `Summarize this conversation in a way suitable for creating a tech support ticket. 
        Keep the length short so that it can be submitted via GET to a form. 
        Respond ONLY with valid JSON in this exact format:
        {
          "subject": "A short, clear subject line (max 100 characters)",
          "details": "A longer description of the issue and conversation summary (max 500 characters)"
        }`;
        
        // Send the summarization request
        const messageData = await this.vcCommunicator.sendaimessage(this.threadId, summaryPrompt);
        
        if (messageData.errorCode) {
          throw new Error(messageData.message || 'Failed to send message');
        }

        const runId = messageData.id;

        // Poll for completion
        const response = await this.pollForCompletion(runId);
        
        // Parse the summary response
        let subject = 'AI Assistant Follow-up';
        let detail = '';
        
        if (response && response.main_answer) {
          try {
            // Try to parse the response as JSON
            const summaryData = JSON.parse(response.main_answer);
            
            if (summaryData.subject) {
              subject = summaryData.subject.substring(0, 100); // Ensure max length
            }
            
            if (summaryData.details) {
              detail = summaryData.details.substring(0, 500); // Ensure max length
            }
          } catch (parseError) {
            console.error('Failed to parse AI response as JSON:', parseError);
            // Fallback to original parsing method
            const content = response.main_answer;
            
            // Look for patterns like "Subject:" or "Title:" followed by content
            const subjectMatch = content.match(/(?:Subject|Title):\s*(.+?)(?:\n|$)/i);
            if (subjectMatch) {
              subject = subjectMatch[1].trim().substring(0, 100);
            } else {
              // If no explicit subject, take the first line or first 50 characters
              const firstLine = content.split('\n')[0];
              subject = firstLine.substring(0, 50).trim();
              if (firstLine.length > 50) subject += '...';
            }
            
            // Look for patterns like "Detail:" or "Description:" or take the whole content
            const detailMatch = content.match(/(?:Detail|Details|Description):\s*(.+)/is);
            if (detailMatch) {
              detail = detailMatch[1].trim().substring(0, 500);
            } else {
              detail = content.substring(0, 500);
            }
          }
        }
        
        // Add thread ID to the detail
        detail += `\n\nThread ID: ${this.threadId}`;
        
        // URL encode the parameters
        const encodedSubject = encodeURIComponent(`AI Follow-Up: ${subject}`);
        const encodedDetail = encodeURIComponent(detail);
        
        // Redirect to the support ticket page
        window.location.href = `/support/new-ticket/?type=AI+Assistant+Follow+up&subject=${encodedSubject}&detail=${encodedDetail}`;
        
      } catch (error) {
        console.error('Error creating support ticket:', error);
        alert('Failed to create support ticket. Please try again.');
      } finally {
        this.isCreatingTicket = false;
      }
    },
    
    clearThread() {
      if (confirm('Are you sure you want to clear this conversation?')) {
        this.threadId = null;
        this.messages = [];
      }
    },
    scrollToBottom() {
      this.$nextTick(() => {
        if (this.$refs.messageContainer) {
          this.$refs.messageContainer.scrollTop = this.$refs.messageContainer.scrollHeight;
        }
      });
    },
    async sendMessage() {
      const question = this.userInput.trim();
      if (!question) return;

      if (!this.vcCommunicator) {
        console.error('VcCommunicator not available');
        return;
      }

      this.messages.push({ role: 'user', content: question });
      this.scrollToBottom(); // Scroll after adding user message
      this.userInput = '';
      this.isThinking = true;

      try {
        // Send message through VcCommunicator
        const messageData = await this.vcCommunicator.sendaimessage(this.threadId || '', question);
        
        if (messageData.errorCode) {
          throw new Error(messageData.message || 'Failed to send message');
        }

        // Extract threadId and runId from response
        // Adjust these based on your actual API response structure
        this.threadId = messageData.thread_id;
        const runId = messageData.id;

        // Poll for completion
        const response = await this.pollForCompletion(runId);
        this.messages.push({ role: 'assistant', content: response });
        this.scrollToBottom(); // Scroll after adding assistant message
      } catch (e) {
        console.error(e);
        this.messages.push({ 
          role: 'assistant', 
          content: { 
            main_answer: 'An error occurred: ' + e.message, 
            paragraphs: [], 
            sources: [] 
          } 
        });
        this.scrollToBottom(); // Scroll after adding error message
      } finally {
        this.isThinking = false;
      }
    },
    async pollForCompletion(runId) {
      while (true) {
        const runStatus = await this.vcCommunicator.getairunstatus(this.threadId, runId);

        if (runStatus.errorCode) {
          throw new Error(runStatus.message || 'Failed to get run status');
        }

        if (runStatus.status === 'completed') break;
        if (runStatus.status === 'failed') throw new Error('Assistant run failed');

        await new Promise((resolve) => setTimeout(resolve, 5000));
      }

      // Get messages
      const msgData = await this.vcCommunicator.getaimessages(this.threadId, null);

      if (msgData.errorCode) {
        throw new Error(msgData.message || 'Failed to get messages');
      }
      
      // Find the latest assistant message
      // Adjust this based on your actual API response structure
      const messages = msgData.messages || msgData.data || [];
      const assistantMsg = messages.find((msg) => msg.role === 'assistant');

      if (!assistantMsg) {
        throw new Error('No assistant response found');
      }

      // Store raw content for debugging
      this.debugRawContent = JSON.stringify(assistantMsg, null, 2);

      try {
        // Extract the text content and annotations
        let textContent = '';
        let annotations = [];
        
        if (assistantMsg.content && assistantMsg.content[0]) {
          if (assistantMsg.content[0].text) {
            textContent = assistantMsg.content[0].text.value || '';
            annotations = assistantMsg.content[0].text.annotations || [];
          } else if (typeof assistantMsg.content[0] === 'string') {
            textContent = assistantMsg.content[0];
          }
        } else if (typeof assistantMsg.content === 'string') {
          textContent = assistantMsg.content;
        }
        
        // Try to parse as JSON first
        const parsedContent = JSON.parse(textContent);
        
        // Process annotations to extract file citations
        const sources = this.processAnnotations(annotations);
        
        // Store debug info
        this.debugFormattedContent = JSON.stringify(parsedContent, null, 2);
        this.debugSources = sources;
        
        // Return the combined response with sources
        return {
          ...parsedContent,
          sources: sources
        };
      } catch {
        // If not JSON, return as plain text
        return {
          main_answer: textContent || assistantMsg.content,
          sources: []
        };
      }
    },
    testFormatting() {
      try {
        const assistantMsg = JSON.parse(this.debugRawContent);
        
        // Extract the text content and annotations
        const textContent = assistantMsg.content[0].text.value;
        const annotations = assistantMsg.content[0].text.annotations || [];
        
        // Parse the JSON response
        const parsedContent = JSON.parse(textContent);
        
        // Process annotations to extract file citations
        const sources = this.processAnnotations(annotations);
        
        // Format the content
        this.debugFormattedContent = this.formatMarkdown(parsedContent.main_answer, sources);
        this.debugSources = sources;
      } catch (error) {
        console.error('Error parsing debug content:', error);
        this.debugFormattedContent = '<div class="text-danger">Error parsing content. Please check the format.</div>';
        this.debugSources = [];
      }
    },
     processAnnotations(annotations) {
      const sources = [];
      
      annotations.forEach(annotation => {
        if (annotation.type === 'file_citation' && annotation.file_citation) {
          const fileId = annotation.file_citation.file_id;
          const fileDetails = websiteFiles.lookup(fileId);
          
          if (fileDetails) {
            sources.push({
              citation: annotation.text,
              fileId: fileId,
              url: fileDetails.source,
              title: fileDetails.title,
              filename: fileDetails.filename
            });
          } else {
            // Fallback if file not found in dictionary
            sources.push({
              citation: annotation.text,
              fileId: fileId,
              url: '#',
              title: 'Unknown source',
              filename: 'Unknown file'
            });
          }
        }
      });
      
      // Remove duplicates based on fileId
      const uniqueSources = sources.filter((source, index, self) =>
        index === self.findIndex((s) => s.fileId === source.fileId)
      );
      
      return uniqueSources;
    },
    formatMarkdown(md, sources = []) {
      // Replace citation annotations with proper links
      let formatted = md;
      
      // Create a map of citations to sources for faster lookup
      const citationMap = new Map();
      if (sources && sources.length > 0) {
        sources.forEach(source => {
          citationMap.set(source.citation, source);
        });
      }
      
      // Replace each citation with a proper link
      formatted = formatted.replace(/【([^】]+)】/g, (match, citation) => {
        const fullCitation = match;
        const source = citationMap.get(fullCitation);
        
        if (source) {
          return `<a href="${source.url}" target="_blank" class="text-decoration-none" title="${source.title}"><sup>[${sources.indexOf(source) + 1}]</sup></a>`;
        }
        return ''; // Remove citation if no source found
      });
      
      // Clean up any double spaces that might have been created
      formatted = formatted.replace(/\s+/g, ' ').trim();
      
      // Handle escaped newlines (convert \n to actual newlines)
      formatted = formatted.replace(/\\n/g, '\n');
      
      // Since the content already contains HTML, we don't need to parse markdown for lists
      // Just apply inline markdown formatting where appropriate
      
      // First, let's protect existing HTML tags from markdown processing
      const htmlPlaceholders = [];
      let placeholderIndex = 0;
      
      // Replace HTML tags with placeholders temporarily
      formatted = formatted.replace(/(<[^>]+>)/g, (match) => {
        const placeholder = `___HTML_PLACEHOLDER_${placeholderIndex}___`;
        htmlPlaceholders[placeholderIndex] = match;
        placeholderIndex++;
        return placeholder;
      });
      
      // Now apply markdown formatting to the remaining text
      // Bold text
      formatted = formatted.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
      // Italic text
      formatted = formatted.replace(/\*([^*]+)\*/g, '<em>$1</em>');
      // Inline code (but not if it's already in a <code> tag)
      formatted = formatted.replace(/(?<!<code>)`([^`]+)`(?!<\/code>)/g, '<code>$1</code>');
      // Links in markdown format [text](url)
      formatted = formatted.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank">$1</a>');
      
      // Restore HTML tags
      htmlPlaceholders.forEach((html, index) => {
        formatted = formatted.replace(`___HTML_PLACEHOLDER_${index}___`, html);
      });
      
      // Handle code blocks (triple backticks)
      formatted = formatted.replace(/```(\w+)?\n([\s\S]*?)```/g, (_, lang, code) => {
        return `<pre><code class="language-${lang || ''}">${this.escapeHTML(code)}</code></pre>`;
      });
      
      // Clean up any remaining escaped HTML entities in non-code sections
      // But preserve them in code blocks
      const codeBlockPlaceholders = [];
      let codeBlockIndex = 0;
      
      // Temporarily replace code blocks
      formatted = formatted.replace(/(<code[^>]*>)([\s\S]*?)(<\/code>)/g, (match) => {
        const placeholder = `___CODE_BLOCK_${codeBlockIndex}___`;
        codeBlockPlaceholders[codeBlockIndex] = match;
        codeBlockIndex++;
        return placeholder;
      });
      
      // Clean up escaped entities outside of code blocks
      formatted = formatted.replace(/&lt;/g, '<').replace(/&gt;/g, '>');
      
      // Restore code blocks
      codeBlockPlaceholders.forEach((block, index) => {
        formatted = formatted.replace(`___CODE_BLOCK_${index}___`, block);
      });
      
      return formatted;
    },
    escapeHTML(str) {
      return str.replace(/</g, '&lt;').replace(/>/g, '&gt;');
    },
  },
  watch: {
    // Optional: Watch for any changes to messages array and auto-scroll
    messages: {
      handler() {
        this.scrollToBottom();
      },
      deep: true
    }
  }
};
</script>

<style scoped>
.card-body pre {
  background-color: #f8f9fa;
  padding: 0.75rem;
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
  overflow-x: auto;
}

/* Style for citation links */
.card-body :deep(a sup) {
  font-size: 0.75rem;
  color: #0066cc;
  font-weight: 600;
}

.card-body :deep(a:hover sup) {
  color: #0052a3;
  text-decoration: underline;
}

/* Style for formatted content */
.formatted-content :deep(p) {
  margin-bottom: 1rem;
}

.formatted-content :deep(ol) {
  margin-bottom: 1rem;
}

.formatted-content :deep(li) {
  margin-bottom: 0.5rem;
}

.formatted-content :deep(code) {
  background-color: #f8f9fa;
  padding: 0.125rem 0.25rem;
  border-radius: 0.25rem;
  color: #e83e8c;
}
</style>
